import React, { useEffect, useState } from "react";
import {
  Card,
  Layout,
  Filters,
  Text,
  BlockStack,
  Spinner,
} from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "configureStore";
import { fetchUserBusinessTrips } from "services/business-trip.service";
import { BusinessTrip } from "types/BusinessTrip";
import Loading from "components/loading/Loading";
import PaginationControls from "components/pagination/PaginationControls";
import {
  setModalxComponent,
  setModalxComponentProps,
} from "components/modalx/modalx.slice";
import { btnPrimaryCls } from "components/tw/classes";
import { tripsStatusLine } from "./trip-next-steps";
import TripTable from "./trip-table";

type BusinessTripsPagination = {
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
};

/**
 * «Komandiruotės» in the cabinet: a line over the list, a main button and the next step
 * of each trip.
 */
function UserBusinessTrips() {
  const dispatch: AppDispatch = useDispatch();
  const { t } = useTranslation();
  const { companyId } = useParams<{ companyId: string }>();
  const { role } = useSelector((state: RootState) => state.authReducer);
  const refreshNonce = useSelector(
    (state: RootState) => state.userBusinessTripsReducer.refreshNonce
  );

  const [paginationState, setPaginationState] = useState({
    page: 1,
    pageSize: 20,
  });
  const [textSearch, setTextSearch] = useState("");
  const [items, setItems] = useState<BusinessTrip[]>([]);
  const [pagination, setPagination] = useState<BusinessTripsPagination | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { page, pageSize } = paginationState;
  const pageSizeOptions = [10, 20, 50, 100];

  useEffect(() => {
    setPaginationState({ page: 1, pageSize: 20 });
    setTextSearch("");
    setItems([]);
    setPagination(null);
    setError(null);
  }, [companyId]);

  useEffect(() => {
    if (refreshNonce > 0) {
      setPaginationState((prev) => ({ ...prev, page: 1 }));
    }
  }, [refreshNonce]);

  useEffect(() => {
    if (!companyId) return;

    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchUserBusinessTrips(companyId, {
          page,
          pageSize,
          ...(textSearch ? { textSearch } : {}),
        });
        if (cancelled) return;

        if (Array.isArray(data)) {
          setItems(data);
          setPagination({
            total: data.length,
            page: 1,
            pageSize: Math.max(data.length, 1),
            totalPages: data.length > 0 ? 1 : 0,
          });
          return;
        }

        setItems(data.entries || []);
        setPagination(data.pagination || null);
      } catch (e: unknown) {
        if (!cancelled) {
          setError(
            e instanceof Error
              ? e.message
              : t("fetch_error", { defaultValue: "Failed to fetch data" })
          );
          setItems([]);
          setPagination(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [companyId, page, pageSize, textSearch, refreshNonce, t]);

  const handleCreateBusinessTrip = () => {
    dispatch(setModalxComponent("BusinessTripForm"));
    dispatch(setModalxComponentProps({ source: "profile" }));
  };

  const handleQueryChange = (value: string) => {
    setTextSearch(value);
    setPaginationState((prev) => ({ ...prev, page: 1 }));
  };

  const handleQueryValueRemove = () => {
    setTextSearch("");
    setPaginationState((prev) => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (nextPage: number) => {
    if (nextPage > 0 && nextPage !== page) {
      setPaginationState((prev) => ({ ...prev, page: nextPage }));
    }
  };

  const handlePageSizeChange = (nextPageSize: number) => {
    if (nextPageSize > 0 && nextPageSize !== pageSize) {
      setPaginationState({ page: 1, pageSize: nextPageSize });
    }
  };

  const tripPath = (trip: BusinessTrip) =>
    `/app/${companyId}/profile/${role?.user}/business-trips/${trip.id}`;

  const totalItems =
    typeof pagination?.total === "number" ? pagination.total : items.length;
  const totalPages =
    typeof pagination?.totalPages === "number"
      ? pagination.totalPages
      : totalItems > 0
        ? Math.ceil(totalItems / pageSize)
        : 0;
  const hasNextPage = totalPages > 0 ? page < totalPages : false;
  const hasPreviousPage = page > 1;
  const shouldShowPagination = Boolean(pagination);

  if (loading && !pagination && items.length === 0) {
    return <Loading />;
  }

  // The line counts the trips on the screen, so it is drawn only when they are all the
  // person's trips: one page, no search, no failed load.
  const statusLine =
    !error && !textSearch && pagination && pagination.totalPages <= 1
      ? tripsStatusLine(items, t)
      : null;

  const appliedFilters: { key: string; label: string; onRemove: () => void }[] =
    [];
  if (textSearch.length > 0) {
    appliedFilters.push({
      key: "textSearch",
      label: `${t("search")}: ${textSearch}`,
      onRemove: handleQueryValueRemove,
    });
  }

  return (
    <Layout>
      <Layout.Section>
        <div style={{ marginTop: "20px" }}>
          <BlockStack gap="400">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <p
                className={`text-base font-semibold ${
                  statusLine?.needsAction ? "text-amber-700" : "text-gray-900"
                }`}
              >
                {statusLine?.text}
              </p>
              <button
                type="button"
                className={`${btnPrimaryCls} w-full md:w-auto`}
                onClick={handleCreateBusinessTrip}
              >
                {t("cabinet_trip_new")}
              </button>
            </div>
            <Card>
              <BlockStack gap="300">
                <Filters
                  queryValue={textSearch}
                  filters={[]}
                  appliedFilters={appliedFilters}
                  onQueryChange={handleQueryChange}
                  onQueryClear={handleQueryValueRemove}
                  onClearAll={handleQueryValueRemove}
                  hideFilters
                />

                {error && (
                  <Text as="p" tone="critical">
                    {error}
                  </Text>
                )}

                {items.length > 0 ? (
                  <>
                    <div style={{ opacity: loading ? 0.6 : 1 }}>
                      <TripTable trips={items} tripPath={tripPath} />
                    </div>
                    {loading && (
                      <div className="flex justify-center py-2">
                        <Spinner
                          size="small"
                          accessibilityLabel={t("loading")}
                        />
                      </div>
                    )}
                    {shouldShowPagination && (
                      <div
                        style={{
                          borderTop: "1px solid rgba(0, 0, 0, 0.06)",
                          padding: "16px",
                        }}
                      >
                        <PaginationControls
                          page={page}
                          pageSize={pageSize}
                          total={totalItems}
                          totalPages={totalPages}
                          hasNextPage={hasNextPage}
                          hasPreviousPage={hasPreviousPage}
                          onPageChange={handlePageChange}
                          onPageSizeChange={handlePageSizeChange}
                          pageSizeOptions={pageSizeOptions}
                          isLoading={loading}
                        />
                      </div>
                    )}
                  </>
                ) : (
                  !loading && (
                    <Text as="p" tone="subdued">
                      {t("no_business_trips", {
                        defaultValue: "No business trips found.",
                      })}
                    </Text>
                  )
                )}
              </BlockStack>
            </Card>
          </BlockStack>
        </div>
      </Layout.Section>
    </Layout>
  );
}

export default UserBusinessTrips;
